import { Box } from "@mui/material";

export const POSTER = "/images/food-tour/hannah-poster.png";
export const TOUR_INK = "#1B3A5F";
export const TOUR_CREAM = "#F7F1E6";
export const TOUR_POWDER = "#D3E7F5";
export const TOUR_POP = "#E25C3A";
export const TOUR_GREEN = "#2F7D4F";
export const tourBodyFont = '"Nunito", "Helvetica Neue", sans-serif';
export const tourDisplayFont = '"Shantell Sans", "Nunito", sans-serif';

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Nunito:wght@500;700;800&family=Shantell+Sans:wght@600;700;800&display=swap";

type CharacterId = "coffee" | "bear" | "pierogi" | "fish" | "bagel";

const ART: Record<CharacterId, string> = {
  coffee: "/images/food-tour/zabars-cut.png",
  bear: "/images/food-tour/economy-cut.png",
  pierogi: "/images/food-tour/veselka-cut.png",
  fish: "/images/food-tour/greengrass-cut.png",
  bagel: "/images/food-tour/russ-cut.png",
};

const MOTION: Record<CharacterId, string> = {
  coffee: "ft-walk 1.8s ease-in-out infinite",
  bear: "ft-dance 1.4s ease-in-out infinite",
  pierogi: "ft-wave 1.8s ease-in-out infinite",
  fish: "ft-swim 2.4s ease-in-out infinite",
  bagel: "ft-hop 1.3s ease-in-out infinite",
};

const motionSx = {
  "@keyframes ft-idle": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-2px)" },
  },
  "@keyframes ft-walk": {
    "0%, 100%": { transform: "translateX(0) rotate(-3deg)" },
    "50%": { transform: "translateX(2px) rotate(3deg)" },
  },
  "@keyframes ft-dance": {
    "0%, 100%": { transform: "translateY(0) rotate(-3deg)" },
    "50%": { transform: "translateY(-4px) rotate(3deg)" },
  },
  "@keyframes ft-wave": {
    "0%, 100%": { transform: "rotate(-3deg)" },
    "50%": { transform: "rotate(4deg) translateY(-2px)" },
  },
  "@keyframes ft-swim": {
    "0%, 100%": { transform: "translateY(0) rotate(-2deg)" },
    "50%": { transform: "translateY(-3px) rotate(3deg)" },
  },
  "@keyframes ft-hop": {
    "0%, 100%": { transform: "translateY(0) scale(1, 1)" },
    "40%": { transform: "translateY(-5px) scale(0.98, 1.03)" },
    "70%": { transform: "translateY(0) scale(1.02, 0.98)" },
  },
  "@keyframes ft-pop": {
    "0%": { transform: "translateY(0) scale(1)" },
    "40%": { transform: "translateY(-6px) scale(1.04)" },
    "100%": { transform: "translateY(0) scale(1)" },
  },
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
} as const;

export function characterFor(name: string | undefined): CharacterId | null {
  const value = (name ?? "").toLowerCase();
  if (value.includes("zabar")) return "coffee";
  if (value.includes("economy") || value.includes("candy")) return "bear";
  if (value.includes("veselka")) return "pierogi";
  if (value.includes("greengrass") || value.includes("barney")) return "fish";
  if (value.includes("russ")) return "bagel";
  return null;
}

export function TourFonts() {
  return <link rel="stylesheet" href={FONT_HREF} />;
}

export function Walker({
  id,
  celebrate = false,
}: {
  id: CharacterId;
  celebrate?: boolean;
}) {
  return (
    <Box
      sx={{
        position: "absolute",
        bottom: "100%",
        width: 34,
        height: 44,
        mb: "-1px",
        pointerEvents: "none",
        animation: "ft-pace 8.5s ease-in-out infinite",
        animationDelay: id === "bear" ? "1s" : id === "pierogi" ? "2s" : id === "fish" ? "3s" : id === "bagel" ? "4s" : "0s",
        "@keyframes ft-pace": {
          "0%, 100%": { left: "8%", transform: "scaleX(1)" },
          "46%": { left: "calc(100% - 56px)", transform: "scaleX(1)" },
          "50%": { left: "calc(100% - 56px)", transform: "scaleX(-1)" },
          "96%": { left: "8%", transform: "scaleX(-1)" },
        },
        "@media (prefers-reduced-motion: reduce)": { animation: "none", left: 16 },
      }}
    >
      <ShopDrawing id={id} alt="" celebrate={celebrate} fill walk />
    </Box>
  );
}

export function ShopDrawing({
  id,
  alt,
  celebrate = false,
  fill = false,
  walk = false,
  active = false,
}: {
  id: CharacterId;
  alt: string;
  celebrate?: boolean;
  fill?: boolean;
  walk?: boolean;
  active?: boolean;
}) {
  return (
    <Box
      component="img"
      src={ART[id]}
      alt={alt}
      sx={{
        display: "block",
        width: "100%",
        height: fill ? "100%" : "auto",
        objectFit: fill ? "contain" : undefined,
        objectPosition: id === "pierogi" ? "center calc(100% - 12px)" : "center bottom",
        transformOrigin: "center bottom",
        animation: active || celebrate ? (walk ? MOTION.coffee : MOTION[id]) : "ft-idle 3.2s ease-in-out infinite",
        ...motionSx,
      }}
    />
  );
}

export function Parade() {
  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: "28px",
        border: `2.5px solid ${TOUR_INK}`,
        bgcolor: "#F4F1EA",
        boxShadow: `6px 6px 0 ${TOUR_INK}`,
      }}
    >
      <Box
        component="img"
        src={POSTER}
        alt="Coffee, the gummy bear, the pierogi, the sturgeon, and the bagel walking the block"
        sx={{ display: "block", width: "100%", height: "auto" }}
      />
    </Box>
  );
}
